/**
 * Фильтр с множественным выбором.
 *
 * Именно множественный, а не выпадающий список с одним значением: техник
 * регулярно смотрит «заказы двух конкретных врачей» или «всё срочное и
 * просроченное разом». С одиночным выбором такие выборки пришлось бы собирать
 * в несколько заходов.
 */

import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useOutsideClose } from './useOutsideClose.js';

/** Ширина списка: `min-w-[13rem]` в разметке — 208 пикселей. */
const MENU_WIDTH = 208;

export interface FilterOption {
  value: string;
  label: string;
  /** Точка слева — для статусов и способов оплаты. */
  color?: string;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  /**
   * Прижимать список к правому краю кнопки, а не к левому.
   *
   * У фильтров, стоящих во второй половине строки, список шириной в 13rem
   * уходил за правый край экрана вместе с половиной вариантов — на телефоне
   * это «Все врачи» и «Оплата». Сторона выбирается в момент открытия: до него
   * положение кнопки может быть любым, строка фильтров переносится.
   */
  const [alignRight, setAlignRight] = useState(false);

  /*
   * Закрытие по нажатию мимо — общим обработчиком, а не своим.
   * Собственный слушал только `mousedown`, и на телефоне список не
   * закрывался вовсе: там сначала приходит `touchstart`, и до мыши дело
   * доходит не всегда.
   */
  const containerRef = useOutsideClose<HTMLDivElement>(open, () => setOpen(false));

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      // MENU_WIDTH — та же величина, что `min-w-[13rem]` в разметке ниже.
      setAlignRight(rect.left + MENU_WIDTH > document.documentElement.clientWidth - 8);
    }
    setOpen(true);
  };

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const active = selected.length > 0;

  /** Подпись показывает выбранное, а не абстрактное «выбрано: 2». */
  const caption = !active
    ? label
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? label)
      : `${label} · ${selected.length}`;

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={toggleOpen}
        /*
          Ширина кнопки ПОСТОЯННА, а длинная подпись обрезается.

          Подпись меняется при выборе — «Все врачи» превращается в «Гущина
          Е.Д.» или «Все клиники · 3», — и кнопка от этого росла и сжималась.
          Соседи прыгали следом, строка фильтров перестраивалась на каждом
          щелчке, и мышь попадала не туда, куда целилась.

          Но постоянная ширина была ОДНА НА ВСЕХ — 10.5rem по самому длинному
          названию. «Все врачи» занимали столько же, сколько «Любой способ
          оплаты», строка тянулась почти во весь экран, а самому длинному
          названию всё равно не хватало и оно обрывалось многоточием. Теперь
          у каждого фильтра своя постоянная ширина — по его собственному
          названию: оно задаётся невидимым двойником ниже. Прыгать по-прежнему
          нечему, зато короткие фильтры стали короткими.
        */
        className="inline-flex w-full items-center gap-1.5 overflow-hidden rounded-md border px-2.5 py-1.5 text-xs md:w-auto"
        style={{
          borderColor: active ? 'var(--otc-accent)' : 'var(--otc-line)',
          color: active ? 'var(--otc-accent)' : 'var(--otc-ink2)',
          /*
            Фон БЕЛЫЙ, как у порядка сортировки рядом.

            Фильтр был прозрачным и лежал прямо на фоне страницы, а стоящий
            вплотную выбор порядка — на белом. Два соседа в одной строке
            выглядели разной породы: будто один можно нажать, а другой нет.
          */
          background: active
            ? 'color-mix(in srgb, var(--otc-accent) 10%, var(--otc-surface))'
            : 'var(--otc-surface)',
        }}
      >
        {/*
          Обрезается подпись, а не кнопка: стрелка всегда на своём месте.

          Двойник под подписью — невидимый и нещёлкаемый — держит ширину по
          НАЗВАНИЮ фильтра. Оба лежат в одной ячейке сетки, и ширину задаёт
          тот, кто шире; у видимой подписи ширина обнулена `min-w-0`, поэтому
          решает всегда двойник. На телефоне кнопка и так во всю ячейку
          сетки — там двойник ни на что не влияет.
        */}
        <span className="grid min-w-0 flex-1">
          {/*
            На телефоне двойника НЕТ.

            Там кнопки стоят в сетке по две в строке и тянутся по ячейке,
            а двойник с `whitespace-nowrap` не умеет сжиматься и распирал
            ячейку по самому длинному названию — «Все доп. элементы» выдавливало
            соседний столбец за край экрана. Ширину на телефоне задаёт сетка,
            и держать её двойником не нужно.
          */}
          <span
            aria-hidden
            className="invisible col-start-1 row-start-1 hidden whitespace-nowrap md:block"
          >
            {label}
          </span>
          <span className="col-start-1 row-start-1 min-w-0 truncate text-left" title={caption}>
            {caption}
          </span>
        </span>
        <ChevronDown size={12} className="flex-shrink-0" />
      </button>

      {open && (
        <div
          className={`absolute top-full z-40 mt-1 max-h-64 min-w-[13rem] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border py-1 shadow-lg ${
            alignRight ? 'right-0' : 'left-0'
          }`}
          style={{ background: 'var(--otc-surface)', borderColor: 'var(--otc-line)' }}
        >
          {active && (
            <button
              onClick={() => onChange([])}
              className="w-full px-3 py-1.5 text-left text-xs text-ink2 hover:bg-black/5"
            >
              Сбросить
            </button>
          )}
          {options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => toggle(option.value)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-black/5"
              >
                <span
                  className="inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border"
                  style={{
                    borderColor: checked ? 'var(--otc-accent)' : 'var(--otc-line)',
                    background: checked ? 'var(--otc-accent)' : 'transparent',
                  }}
                >
                  {checked && <Check size={9} color="#fff" />}
                </span>
                {option.color && (
                  <span
                    className="h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ background: option.color }}
                  />
                )}
                <span className="text-ink">{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
